import ExcelJS from "exceljs";
import dayjs from "dayjs";

/**
 * index    – the leading "#" numbering column, kept narrow
 * text     – mostly non-numeric values (team names), takes the free width
 * question – per-question columns whose header is a number
 * total    – other numeric columns ("Xal", "Reytinq", …), right-aligned
 */
export type ColumnKind = "index" | "text" | "question" | "total";

export interface SheetTab {
  name: string;
  /** Title rows above the table header, e.g. the tournament name. */
  captions: string[];
  header: string[];
  body: string[][];
  columns: ColumnKind[];
}

export interface LoadedSheet {
  title: string;
  tabs: SheetTab[];
}

export class SheetLoadError extends Error {}

const SHEET_ID_PATTERN = /^[a-zA-Z0-9-_]{20,}$/;

export const sheetUrl = (sheetId: string) => `https://docs.google.com/spreadsheets/d/${sheetId}`;

/**
 * Accepts only a full Google Sheets link
 * (https://docs.google.com/spreadsheets/d/<id>/edit#gid=0) and returns the ID.
 * Bare SheetIDs are rejected.
 */
export const extractSheetId = (input: string): string | null => {
  let url: URL;
  try {
    url = new URL(input.trim());
  } catch {
    return null;
  }
  if (url.hostname !== "docs.google.com") return null;
  const match = url.pathname.match(/^\/spreadsheets\/d\/([a-zA-Z0-9-_]+)/);
  return match && SHEET_ID_PATTERN.test(match[1]) ? match[1] : null;
};

const cellToText = (cell: ExcelJS.Cell): string => {
  // Merged cells repeat the master's value; show it only once.
  if (cell.isMerged && cell.master !== cell) return "";

  let value: unknown = cell.value;
  if (value && typeof value === "object" && "result" in (value as any)) {
    value = (value as ExcelJS.CellFormulaValue).result;
  }

  if (value === null || value === undefined) return "";
  if (value instanceof Date) {
    const hasTime = value.getUTCHours() !== 0 || value.getUTCMinutes() !== 0;
    // ExcelJS returns spreadsheet dates as UTC timestamps of the local wall time.
    return dayjs(value.toISOString().slice(0, 19)).format(hasTime ? "DD.MM.YYYY HH:mm" : "DD.MM.YYYY");
  }
  if (typeof value === "object" && "error" in (value as any)) {
    return String((value as ExcelJS.CellErrorValue).error);
  }

  return cell.text ?? String(value);
};

const worksheetToRows = (worksheet: ExcelJS.Worksheet): string[][] => {
  const rows: string[][] = [];
  const columnCount = worksheet.columnCount;

  for (let r = 1; r <= worksheet.rowCount; r++) {
    const row = worksheet.getRow(r);
    const values: string[] = [];
    for (let c = 1; c <= columnCount; c++) {
      values.push(cellToText(row.getCell(c)));
    }
    rows.push(values);
  }

  // Drop trailing empty rows and columns that Sheets exports as padding.
  while (rows.length && rows[rows.length - 1].every((v) => v === "")) rows.pop();
  let lastColumn = 0;
  rows.forEach((row) => {
    for (let c = row.length - 1; c >= lastColumn; c--) {
      if (row[c] !== "") {
        lastColumn = c + 1;
        break;
      }
    }
  });

  return rows.map((row) => row.slice(0, lastColumn));
};

const NUMBER_PATTERN = /^-?\d+([.,]\d+)?$/;

const classifyColumn = (header: string, values: string[], index: number): ColumnKind => {
  const filled = values.filter((v) => v !== "");
  const numeric = filled.filter((v) => NUMBER_PATTERN.test(v));
  if (index === 0 && numeric.length === filled.length) return "index";
  if (numeric.length * 2 < filled.length) return "text";
  return /^\d+$/.test(header) ? "question" : "total";
};

const toTab = (name: string, rows: string[][]): SheetTab => {
  // Rows with at most one value before the real header are titles (usually merged cells).
  const headerIndex = rows.findIndex((row) => row.filter((v) => v !== "").length > 1);
  if (headerIndex === -1) {
    return { name, captions: rows.flat().filter(Boolean), header: [], body: [], columns: [] };
  }

  const captions = rows.slice(0, headerIndex).flat().filter(Boolean);
  const header = rows[headerIndex].map((v) => v.trim());
  const body = rows.slice(headerIndex + 1);
  const columns = header.map((h, c) =>
    classifyColumn(
      h,
      body.map((row) => row[c] ?? ""),
      c
    )
  );
  return { name, captions, header, body, columns };
};

/** Reads the spreadsheet's own name from the export's Content-Disposition header. */
const titleFromResponse = (response: Response): string => {
  const disposition = response.headers.get("content-disposition") ?? "";
  const encoded = disposition.match(/filename\*=UTF-8''([^;]+)/i);
  let name = "";
  if (encoded) {
    try {
      name = decodeURIComponent(encoded[1]);
    } catch {
      // Fall back to the plain filename below.
    }
  }
  if (!name) name = disposition.match(/filename="([^"]+)"/i)?.[1] ?? "";
  return name.replace(/\.xlsx$/i, "").trim();
};

/**
 * Downloads a Google Sheet as XLSX via the public export endpoint and returns
 * every tab. The sheet must be shared as "Anyone with the link can view".
 */
export const loadGoogleSheet = async (sheetId: string): Promise<LoadedSheet> => {
  let response: Response;
  try {
    response = await fetch(
      `https://docs.google.com/spreadsheets/d/${encodeURIComponent(sheetId)}/export?format=xlsx`,
      { cache: "no-store" }
    );
  } catch {
    // Private sheets redirect to a login page without CORS headers.
    throw new SheetLoadError(
      "Cədvələ daxil olmaq mümkün olmadı. Cədvəlin \"Linki olan hər kəs baxa bilər\" kimi paylaşıldığından əmin olun."
    );
  }

  if (response.status === 404) {
    throw new SheetLoadError("Bu linkə uyğun cədvəl tapılmadı.");
  }
  const contentType = response.headers.get("content-type") ?? "";
  if (!response.ok || !contentType.includes("spreadsheetml")) {
    throw new SheetLoadError(
      "Cədvəl yüklənmədi. Cədvəlin \"Linki olan hər kəs baxa bilər\" kimi paylaşıldığından əmin olun."
    );
  }

  const workbook = new ExcelJS.Workbook();
  await workbook.xlsx.load(await response.arrayBuffer());

  const tabs = workbook.worksheets
    .filter((worksheet) => worksheet.state !== "hidden" && worksheet.state !== "veryHidden")
    .map((worksheet) => toTab(worksheet.name, worksheetToRows(worksheet)));

  return { title: titleFromResponse(response) || tabs[0]?.captions[0] || "", tabs };
};
